import { CreditCard, Link2, Mail, RotateCcw, Send, Settings2, UserPlus, Wallet } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardSubtitle,
  CardTitle,
  Input,
  Select,
  Skeleton,
  Switch,
} from '@/components/ui';
import { useNotify } from '@/hooks/useNotify';
import { useT } from '@/i18n';
import { api, type AdminSetting } from '@/lib/api';

/**
 * Betriebs-Einstellungen im Panel.
 *
 * Jeder Wert hat die .env als Rueckfallebene: solange hier nichts gesetzt ist,
 * gilt der Serverwert. "Zuruecksetzen" entfernt die Ueberschreibung wieder.
 * Geheimnisse kommen nie zurueck - nur "gesetzt" plus die letzten Zeichen.
 */

const GROUPS: Array<{ id: string; icon: React.ReactNode; keys: string[] }> = [
  {
    id: 'smtp',
    icon: <Mail size={16} />,
    keys: [
      'smtp.host',
      'smtp.port',
      'smtp.secure',
      'smtp.user',
      'smtp.password',
      'smtp.allowInsecureTls',
      'mail.from',
      'mail.fromName',
      'mail.replyTo',
      'mail.bcc',
      'mail.publicUrl',
    ],
  },
  {
    id: 'paypal',
    icon: <CreditCard size={16} />,
    keys: [
      'paypal.mode',
      'paypal.clientId',
      'paypal.clientSecret',
      'paypal.webhookId',
      'paypal.minTopupCents',
      'paypal.maxTopupCents',
    ],
  },
  {
    id: 'whmcs',
    icon: <Link2 size={16} />,
    keys: ['whmcs.apiToken', 'whmcs.suppressMails'],
  },
  {
    id: 'registration',
    icon: <UserPlus size={16} />,
    keys: ['registration.open', 'registration.requireVerifiedEmail'],
  },
  {
    id: 'billing',
    icon: <Wallet size={16} />,
    keys: [
      'billing.taxRatePermille',
      'billing.defaultCreditLimitCents',
      'billing.lowBalanceCents',
      'billing.suspendGraceDays',
      'billing.deleteRetentionHours',
      'billing.invoiceDueDays',
    ],
  },
];

/** i18n-Schluessel duerfen keine Punkte im Namensteil tragen. */
const keyToken = (key: string) => key.replace(/\./g, '_');

export function AdminSettingsPage() {
  const t = useT();
  const notify = useNotify();

  const [settings, setSettings] = useState<AdminSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Record<string, string | number | boolean | null>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.admin.settings();
      setSettings(data.settings);
      setDraft({});
    } catch (cause) {
      notify.fromError(cause, t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const byKey = useMemo(() => new Map(settings.map((entry) => [entry.key, entry])), [settings]);
  const dirty = Object.keys(draft).length > 0;

  const save = async () => {
    setBusy(true);
    try {
      const data = await api.admin.saveSettings(draft);
      setSettings(data.settings);
      setDraft({});
      notify.success(t('settingsAdmin.saved'));
    } catch (cause) {
      notify.fromError(cause, t('errors.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const testMail = async () => {
    setBusy(true);
    try {
      const result = await api.admin.testMail();
      notify.success(t('settingsAdmin.mailOk', { to: result.to }));
    } catch (cause) {
      notify.fromError(cause, t('settingsAdmin.mailFailed'));
    } finally {
      setBusy(false);
    }
  };

  const testPaypal = async () => {
    setBusy(true);
    try {
      const result = await api.admin.testPaypal();
      if (result.ok) notify.success(t('settingsAdmin.paypalOk', { mode: result.mode }));
      else notify.error(t('settingsAdmin.paypalFailed', { message: result.message ?? '' }));
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Skeleton className="h-64" />;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2">
          <Settings2 size={18} className="mt-0.5 text-t2" />
          <div>
            <h2 className="text-[16px] font-bold text-t0">{t('settingsAdmin.title')}</h2>
            <p className="text-[13px] text-t1">{t('settingsAdmin.subtitle')}</p>
          </div>
        </div>
        <Button onClick={() => void save()} disabled={busy || !dirty}>
          {dirty ? t('settingsAdmin.saveChanges', { count: Object.keys(draft).length }) : t('common.save')}
        </Button>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        {GROUPS.map((group) => (
          <Card key={group.id}>
            <CardHeader>
              <div>
                <CardTitle className="flex items-center gap-2">
                  {group.icon}
                  {t(`settingsAdmin.group_${group.id}` as never)}
                </CardTitle>
                <CardSubtitle>{t(`settingsAdmin.group_${group.id}_hint` as never)}</CardSubtitle>
              </div>
              {group.id === 'smtp' && (
                <Button size="sm" variant="outline" icon={<Send size={13} />} disabled={busy} onClick={() => void testMail()}>
                  {t('settingsAdmin.testMail')}
                </Button>
              )}
              {group.id === 'paypal' && (
                <Button size="sm" variant="outline" icon={<Send size={13} />} disabled={busy} onClick={() => void testPaypal()}>
                  {t('settingsAdmin.testPaypal')}
                </Button>
              )}
            </CardHeader>

            <div className="space-y-3">
              {group.keys.map((key) => {
                const setting = byKey.get(key);
                if (!setting) return null;
                return (
                  <SettingRow
                    key={key}
                    setting={setting}
                    draftValue={draft[key]}
                    onChange={(value) => setDraft((current) => ({ ...current, [key]: value }))}
                    onReset={() => setDraft((current) => ({ ...current, [key]: null }))}
                  />
                );
              })}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function SettingRow({
  setting,
  draftValue,
  onChange,
  onReset,
}: {
  setting: AdminSetting;
  draftValue: string | number | boolean | null | undefined;
  onChange: (value: string | number | boolean) => void;
  onReset: () => void;
}) {
  const t = useT();
  const label = t(`settingsAdmin.k_${keyToken(setting.key)}` as never) || setting.key;

  const pendingReset = draftValue === null;
  const effective =
    draftValue !== undefined && draftValue !== null
      ? draftValue
      : setting.overridden
        ? setting.value
        : setting.envValue;

  const overriddenNow = !pendingReset && (draftValue !== undefined || setting.overridden);

  const fallbackHint =
    setting.type === 'secret'
      ? setting.envValue === true
        ? t('settingsAdmin.envSet')
        : t('settingsAdmin.envEmpty')
      : String(setting.envValue) === ''
        ? t('settingsAdmin.envEmpty')
        : t('settingsAdmin.envIs', { value: String(setting.envValue) });

  return (
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-[12.5px] font-semibold text-t1">{label}</span>
          {overriddenNow && <Badge variant="accent">{t('settingsAdmin.overridden')}</Badge>}
          {pendingReset && <Badge variant="warning">{t('settingsAdmin.willReset')}</Badge>}
        </div>

        {setting.type === 'bool' ? (
          <Switch
            checked={Boolean(effective)}
            onChange={(value) => onChange(value)}
            label={Boolean(effective) ? t('common.yes') : t('common.no')}
          />
        ) : setting.choices ? (
          <Select value={String(effective ?? '')} onChange={(event) => onChange(event.target.value)}>
            {setting.choices.map((choice) => (
              <option key={choice} value={choice}>
                {choice}
              </option>
            ))}
          </Select>
        ) : setting.type === 'secret' ? (
          <Input
            type="password"
            autoComplete="new-password"
            placeholder={setting.secretHint ?? t('settingsAdmin.secretPlaceholder')}
            value={typeof draftValue === 'string' ? draftValue : ''}
            onChange={(event) => onChange(event.target.value)}
          />
        ) : (
          <Input
            type={setting.type === 'int' ? 'number' : 'text'}
            value={pendingReset ? '' : String(effective ?? '')}
            onChange={(event) =>
              onChange(setting.type === 'int' ? Number(event.target.value) : event.target.value)
            }
          />
        )}

        <p className="mt-1 text-[11.5px] text-t2">{fallbackHint}</p>
      </div>

      {(setting.overridden || draftValue !== undefined) && !pendingReset && (
        <button
          type="button"
          title={t('settingsAdmin.reset')}
          aria-label={t('settingsAdmin.reset')}
          onClick={onReset}
          className="mt-6 flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-vela-sm)] text-t2 hover:bg-bg-3 hover:text-t0"
        >
          <RotateCcw size={13} />
        </button>
      )}
    </div>
  );
}
